"use client";
import React, {
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useState,
  Component,
} from "react";
import dynamic from "next/dynamic";
import styles from "./index.module.scss";
import { isApp } from "@/app/bridge";
import { shouldUseWebview } from "@/app/utils/url";
import {
  supportsStreamdown,
  supportsRegExpLookbehind,
  isMobileDevice,
} from "@/app/utils/device";
import { ImagePreview } from "@douyinfe/semi-ui";
import { ImageViewer } from "antd-mobile";
import TypewriterFallback from "./TypewriterFallback";
import { useMessageStore } from "@/app/store";

const LOG_PREFIX = "[AiAnswer]";

const imgDimensionCache = new Map<string, { width: number; height: number }>();

const LazyStreamdown = dynamic(
  () =>
    import("streamdown")
      .then((mod) => {
        console.info(LOG_PREFIX, "Streamdown module loaded successfully");
        return { default: mod.Streamdown };
      })
      .catch((err) => {
        console.error(
          LOG_PREFIX,
          "Streamdown module failed to load:",
          err?.message || err,
          "\nStack:",
          err?.stack,
        );
        throw err;
      }),
  {
    ssr: false,
    loading: () => null,
  },
);

// ── ErrorBoundary：捕获 Streamdown 渲染时的异常 ──
interface EBProps {
  onError: (error: Error, errorInfo: React.ErrorInfo) => void;
  fallback: React.ReactNode;
  children: React.ReactNode;
}
interface EBState {
  hasError: boolean;
}
class StreamdownErrorBoundary extends Component<EBProps, EBState> {
  state: EBState = { hasError: false };

  static getDerivedStateFromError(): EBState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.props.onError(error, errorInfo);
  }

  render() {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}

interface Props {
  content: string;
  streaming?: boolean;
  error?: boolean;
  /** 历史记录等仅静态展示：关闭 Streamdown/打字机动效 */
  staticDisplay?: boolean;
  onContentUpdate?: () => void;
  onTypingEnd?: () => void;
}

/**
 * AI回复组件 - 使用 Streamdown 实现流式 Markdown 渲染
 *
 * 状态说明：
 * - streaming=true: SSE 数据正在接收中，Streamdown 处于动画模式
 * - streaming=false: SSE 数据接收完毕
 * - error=true: 出错，立即静态显示全部内容
 * - staticDisplay=true: 历史/只读，无流式与打字机动效（与 streaming=false 配合使用）
 *
 * 兼容性回退：
 * 当浏览器不支持 RegExp lookbehind（如 iOS 15.x Safari）时，
 * Streamdown 的依赖 remend 会在加载时抛出 SyntaxError，
 * 此时自动切换为 markdown-it + 打字机动画方案。
 */
export default function AiAnswerQuestion({
  content,
  streaming = true,
  error = false,
  staticDisplay = false,
  onContentUpdate,
  onTypingEnd,
}: Props) {
  const onContentUpdateRef = useRef(onContentUpdate);
  const onTypingEndRef = useRef(onTypingEnd);
  const hasCalledTypingEndRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { aiTypingEnd } = useMessageStore();
  const [useStreamdown, setUseStreamdown] = useState<boolean | null>(null);
  const [renderFailed, setRenderFailed] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewSrc, setPreviewSrc] = useState("");

  useEffect(() => {
    const supported = supportsStreamdown();
    const lookbehind = supportsRegExpLookbehind();
    console.log("Compatibility check:", {
      supportsRegExpLookbehind: lookbehind,
      supportsStreamdown: supported,
      userAgent: navigator.userAgent,
    });
    setUseStreamdown(supported);
  }, []);

  const handleStreamdownRenderError = useCallback(
    (err: Error, errorInfo: React.ErrorInfo) => {
      console.error(
        LOG_PREFIX,
        "Streamdown render error, falling back to typewriter:",
        err?.message || err,
        "\nStack:",
        err?.stack,
        "\nComponent stack:",
        errorInfo?.componentStack,
      );
      console.log("errorInfo", errorInfo);
      setRenderFailed(true);
    },
    [],
  );

  useEffect(() => {
    onContentUpdateRef.current = onContentUpdate;
    onTypingEndRef.current = onTypingEnd;
  });

  useEffect(() => {
    if (!content) {
      hasCalledTypingEndRef.current = false;
    }
  }, [content]);

  const notifyTypingEnd = useCallback(() => {
    if (!hasCalledTypingEndRef.current && content) {
      hasCalledTypingEndRef.current = true;
      onTypingEndRef.current?.();
    }
  }, [content]);

  useEffect(() => {
    if (content) {
      onContentUpdateRef.current?.();
    }
  }, [content]);

  useEffect(() => {
    if (error && content) {
      notifyTypingEnd();
    }
  }, [error, content, notifyTypingEnd]);

  useEffect(() => {
    if (staticDisplay && content && !error) {
      notifyTypingEnd();
    }
  }, [staticDisplay, content, error, notifyTypingEnd]);

  useEffect(() => {
    if (aiTypingEnd && content) {
      notifyTypingEnd();
    }
  }, [aiTypingEnd, content, notifyTypingEnd]);

  const handleAnimationEnd = useCallback(() => {
    notifyTypingEnd();
  }, [notifyTypingEnd]);

  const openImagePreviewRef = useRef((src: string) => {
    setPreviewSrc(src);
    setPreviewVisible(true);
  });

  // TypewriterFallback 使用 dangerouslySetInnerHTML，无法绑定 React 事件，
  // 通过 MutationObserver 为动态插入的 <img> 直接绑定 onclick
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const bindImg = (img: HTMLImageElement) => {
      if (img.dataset.previewBound) return;
      img.dataset.previewBound = "1";
      img.style.cursor = "pointer";
      img.onclick = (e) => {
        if (img.parentElement?.tagName === "A") return;
        const src = img.getAttribute("src");
        if (src && /^https?:\/\//i.test(src)) {
          e.preventDefault();
          e.stopPropagation();
          openImagePreviewRef.current(src);
        }
      };
    };

    container.querySelectorAll<HTMLImageElement>("img").forEach(bindImg);

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (node instanceof HTMLImageElement) {
            bindImg(node);
          } else if (node instanceof HTMLElement) {
            node.querySelectorAll<HTMLImageElement>("img").forEach(bindImg);
          }
        });
      }
    });
    observer.observe(container, { childList: true, subtree: true });

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useStreamdown, renderFailed]);

  const urlTransformFn = useCallback(
    (url: string, key: string, node: { tagName: string }) => {
      console.log("[urlTransformFn]", { url, key, tagName: node.tagName });
      if (isApp() && node.tagName === "a" && !shouldUseWebview(url)) {
        return `bambulab://bbl/webview?urlString=${encodeURIComponent(url)}`;
      }
      try {
        return new URL(url).href;
      } catch {
        return url;
      }
    },
    [],
  );

  const customComponents = useMemo(
    () => ({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      a: ({ node, ...props }: any) => (
        <a {...props} target="_blank" rel="noopener noreferrer" />
      ),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      img: ({ node, ...props }: any) => {
        const src = props.src;
        const isPreviewable =
          src && /^https?:\/\//i.test(src) && !props["data-no-preview"];
        const cached = src ? imgDimensionCache.get(src) : undefined;
        const dimensionAttrs =
          cached && !props.width && !props.height
            ? { width: cached.width, height: cached.height }
            : {};
        return (
          <img
            {...props}
            {...dimensionAttrs}
            style={{
              ...(props.style || {}),
              ...(isPreviewable ? { cursor: "pointer" } : {}),
            }}
            onLoad={(e: React.SyntheticEvent<HTMLImageElement>) => {
              const img = e.currentTarget;
              if (src && img.naturalWidth && !imgDimensionCache.has(src)) {
                imgDimensionCache.set(src, {
                  width: img.naturalWidth,
                  height: img.naturalHeight,
                });
              }
            }}
            onClick={
              isPreviewable
                ? (e: React.MouseEvent) => {
                    if (
                      (e.target as HTMLElement).parentElement?.tagName === "A"
                    )
                      return;
                    e.preventDefault();
                    e.stopPropagation();
                    openImagePreviewRef.current(src);
                  }
                : undefined
            }
          />
        );
      },
    }),
    [],
  );

  const allowedTags = useMemo(
    () => ({
      iframe: [
        "src",
        "style",
        "frameborder",
        "allowfullscreen",
        "width",
        "height",
        "scrolling",
        "allow",
        "title",
      ],
      div: ["style", "class"],
      span: ["style", "class"],
      img: ["src", "alt", "style", "width", "height", "loading"],
      table: ["style", "class"],
      td: ["style", "colspan", "rowspan"],
      th: ["style", "colspan", "rowspan"],
      tr: ["style"],
      thead: ["style"],
      tbody: ["style"],
      video: [
        "src",
        "style",
        "controls",
        "width",
        "height",
        "autoplay",
        "muted",
        "loop",
        "poster",
      ],
      source: ["src", "type"],
    }),
    [],
  );

  const processedContent = useMemo(() => {
    if (!content) return "";
    let result = content.replace(/<br\/>/g, "\n");
    result = result.replace(
      /!\[([^\]]*)\]\(([^)\s]+)(?:\s+=(\d*)x(\d*))?\s*\)/g,
      (match, alt, url, width, height) => {
        if (!width && !height) {
          try {
            const parsed = new URL(url);
            if (!/^https?:$/i.test(parsed.protocol)) return match;
            const normalizedUrl = parsed.href;
            if (normalizedUrl !== url) {
              return `![${alt}](${normalizedUrl})`;
            }
          } catch {
            // invalid URL, leave as-is
          }
          return match;
        }
        const safeUrl = (() => {
          try {
            const parsed = new URL(url);
            if (!/^https?:$/i.test(parsed.protocol)) return url;
            return parsed.href;
          } catch {
            return url;
          }
        })();
        const safeAlt = alt
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;");
        let tag = `<img src="${safeUrl}" alt="${safeAlt}"`;
        if (width) tag += ` width="${width}"`;
        if (height) tag += ` height="${height}"`;
        tag += ` />`;
        return tag;
      },
    );
    return result;
  }, [content]);

  const isMobile = useMemo(() => isMobileDevice(), []);

  const showLoading = !content && !error;

  if (showLoading) {
    return (
      <div className={styles.aiAnswerContainer}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingDot}></div>
          <div className={styles.loadingDot}></div>
          <div className={styles.loadingDot}></div>
        </div>
      </div>
    );
  }

  if (useStreamdown === null) {
    return <div className={styles.aiAnswerContainer} />;
  }

  const imagePreviewNode = isMobile ? (
    <ImageViewer
      image={previewSrc}
      visible={previewVisible}
      onClose={() => setPreviewVisible(false)}
    />
  ) : (
    <ImagePreview
      onClose={() => setPreviewVisible(false)}
      src={previewSrc}
      visible={previewVisible}
      onVisibleChange={setPreviewVisible}
    />
  );
  // 低版本浏览器或 Streamdown 渲染失败：使用打字机回退
  if (!useStreamdown || renderFailed) {
    return (
      <>
        <div className={styles.aiAnswerContainer} ref={containerRef}>
          <TypewriterFallback
            content={processedContent}
            streaming={streaming}
            error={error}
            instant={staticDisplay}
            onContentUpdate={onContentUpdate}
            onTypingEnd={onTypingEnd}
          />
        </div>
        {imagePreviewNode}
      </>
    );
  }

  return (
    <>
      <div className={styles.aiAnswerContainer} ref={containerRef}>
        <div className={styles.aiAnswerContent}>
          <StreamdownErrorBoundary
            onError={handleStreamdownRenderError}
            fallback={
              <TypewriterFallback
                content={processedContent}
                streaming={streaming}
                error={error}
                instant={staticDisplay}
                onContentUpdate={onContentUpdate}
                onTypingEnd={onTypingEnd}
              />
            }
          >
            <LazyStreamdown
              animated={!error && !staticDisplay && !aiTypingEnd}
              isAnimating={
                streaming && !error && !staticDisplay && !aiTypingEnd
              }
              components={customComponents}
              urlTransform={urlTransformFn}
              onAnimationEnd={handleAnimationEnd}
              allowedTags={allowedTags}
              normalizeHtmlIndentation
              controls={{
                table: { copy: false, download: false, fullscreen: false },
              }}
            >
              {processedContent}
            </LazyStreamdown>
          </StreamdownErrorBoundary>
        </div>
      </div>
      {imagePreviewNode}
    </>
  );
}
